import { createAsyncThunk, createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  BrickSizes,
  Joint,
  JointClass,
  JOINTS,
  JointSize,
  StructureType,
} from '../../constants';
import { WallConfig } from '../../types/wall';
import { BrickSeries, prepareSeries, selectFailedImages, selectUsableBrickSeries } from './bricksSlice';
import { RootState } from '../index';
import { layoutWallTile } from '../../wall/layout';
import { SharedWall } from '../../share/wallLink';

interface ConfigurationState {
  scale: number;
  seed: number;
  selectedBricks: Record<string, number>; // brickId -> rate
  structure: StructureType;
  brickSize: BrickSizes;
  joint: JointClass;
  verticalJointSize: JointSize;
  horizontalJointSize: JointSize;
}

const newSeed = () => crypto.getRandomValues(new Uint32Array(1))[0];

const initialState: ConfigurationState = {
  scale: 1,
  seed: newSeed(),
  selectedBricks: {},
  structure: StructureType.Wild,
  brickSize: BrickSizes.WF,
  joint: JOINTS.find((joint) => joint.name === '930') ?? JOINTS[0],
  verticalJointSize: JointSize.Glued,
  horizontalJointSize: JointSize.Glued,
};

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    addBrick: (state, action: PayloadAction<string>) => {
      const brickId = action.payload;
      if (!state.selectedBricks[brickId]) {
        const currentBricks = Object.keys(state.selectedBricks);
        const rate = 100 / (currentBricks.length + 1);
        
        // Set new brick rate
        state.selectedBricks[brickId] = rate;
        
        // Update all existing brick rates
        currentBricks.forEach(id => {
          state.selectedBricks[id] = rate;
        });
      }
    },
    
    removeBrick: (state, action: PayloadAction<string>) => {
      const brickId = action.payload;
      const rate = state.selectedBricks[brickId];
      delete state.selectedBricks[brickId];
      
      const remainingBricks = Object.keys(state.selectedBricks);
      if (remainingBricks.length > 0) {
        const additionalRate = rate / remainingBricks.length;
        remainingBricks.forEach(id => {
          state.selectedBricks[id] += additionalRate;
        });
      }
    },
    
    changeRate: (state, action: PayloadAction<{ brickId: string; rate: number }>) => {
      const { brickId, rate } = action.payload;
      state.selectedBricks[brickId] = rate;
      
      const selectedBrickIds = Object.keys(state.selectedBricks);
      if (selectedBrickIds.length === 2) {
        const otherBrickId = selectedBrickIds.find(id => id !== brickId);
        if (otherBrickId) {
          state.selectedBricks[otherBrickId] = 100 - rate;
        }
      }
    },
    
    setStructure: (state, action: PayloadAction<StructureType>) => {
      state.structure = action.payload;
    },
    
    setHorizontalJointSize: (state, action: PayloadAction<JointSize>) => {
      state.horizontalJointSize = action.payload;
    },
    
    setVerticalJointSize: (state, action: PayloadAction<JointSize>) => {
      state.verticalJointSize = action.payload;
    },
    
    setJoint: (state, action: PayloadAction<JointClass>) => {
      state.joint = action.payload;
    },
    
    setBrickSize: (state, action: PayloadAction<BrickSizes>) => {
      state.brickSize = action.payload;
    },
    
    setScale: (state, action: PayloadAction<number>) => {
      state.scale = action.payload;
    },

    shuffle: {
      reducer: (state, action: PayloadAction<number>) => {
        state.seed = action.payload;
      },
      prepare: () => ({ payload: newSeed() }),
    },

    // Takes over a wall from a share link.
    restoreWall: (state, action: PayloadAction<Partial<SharedWall>>) => {
      Object.assign(state, action.payload);
    },
  },
});

// Selectors
export const selectScale = (state: RootState) => state.configuration.scale;
export const selectSeed = (state: RootState) => state.configuration.seed;
export const selectSelectedBricks = (state: RootState) => state.configuration.selectedBricks;
export const selectStructure = (state: RootState) => state.configuration.structure;
export const selectBrickSize = (state: RootState) => state.configuration.brickSize;
export const selectJoint = (state: RootState) => state.configuration.joint;
export const selectVerticalJointSize = (state: RootState) => state.configuration.verticalJointSize;
export const selectHorizontalJointSize = (state: RootState) => state.configuration.horizontalJointSize;

export const selectSelectedBricksList = createSelector(
  [
    (state: RootState) => state.configuration.selectedBricks,
    (state: RootState) => state.bricks.brickSeries,
  ],
  (selectedBricks, brickSeries): BrickSeries[] =>
    Object.keys(selectedBricks)
      .map((id) => brickSeries[id])
      .filter(Boolean)
);

export const selectConfig = createSelector(
  [
    selectSelectedBricksList,
    (state: RootState) => state.configuration.brickSize,
    (state: RootState) => state.configuration.scale,
    (state: RootState) => state.configuration.structure,
    (state: RootState) => state.configuration.joint,
    (state: RootState) => state.configuration.horizontalJointSize,
    (state: RootState) => state.configuration.verticalJointSize,
    (state: RootState) => state.configuration.selectedBricks,
  ],
  (bricks, brickSize, scale, structure, joint, horizontal, vertical, brickSelection): WallConfig => ({
    bricks,
    brickSelection,
    brickSize,
    scale,
    structure,
    joint,
    jointSize: {
      [Joint.Horizontal]: horizontal,
      [Joint.Vertical]: vertical,
    },
  })
);

export const {
  addBrick,
  removeBrick,
  changeRate,
  setStructure,
  setHorizontalJointSize,
  setVerticalJointSize,
  setJoint,
  setBrickSize,
  setScale,
  shuffle,
  restoreWall,
} = configurationSlice.actions;

// Loads the images of brick series that were selected before the brick series
// arrived (from a share link). A series the organization no longer shows is
// dropped, and its coverage rate goes to the others.
export const prepareSelectedSeries = createAsyncThunk<void, void, { state: RootState }>(
  'configuration/prepareSelectedSeries',
  async (_arg, { dispatch, getState }) => {
    const { configuration, bricks } = getState();
    const [known, unknown] = _.partition(
      Object.keys(configuration.selectedBricks),
      (id) => bricks.brickSeries[id]
    );
    unknown.forEach((id) => dispatch(removeBrick(id)));
    await Promise.all(known.map((id) => dispatch(prepareSeries(id))));
  }
);

export const selectSharedWall = (state: RootState): SharedWall => state.configuration;

export const selectWallLayout = createSelector(
  [
    selectSelectedBricks,
    selectBrickSize,
    selectStructure,
    selectHorizontalJointSize,
    selectVerticalJointSize,
    selectSeed,
    selectUsableBrickSeries,
  ],
  (brickSelection, brickSize, structure, horizontal, vertical, seed, brickSeries) =>
    layoutWallTile(
      {
        brickSelection,
        brickSize,
        structure,
        jointSize: { [Joint.Horizontal]: horizontal, [Joint.Vertical]: vertical },
      },
      seed,
      brickSeries
    )
);

export default configurationSlice.reducer;
export const selectMissingImages = createSelector(
  [selectSelectedBricksList, selectFailedImages],
  (series, failedImages): string[] => {
    const failed = new Set(failedImages);
    return series.flatMap((s) =>
      s.bricks.map((b) => b.displayImage).filter((src) => failed.has(src))
    );
  }
);
